import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faArchive, faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, Grid, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'
import {
  digObject, getQueryParamsFromHash, parsePermittedQueryParams,
} from '@domain-group/javascript-utils'

import type { EntitiesState } from '@redux/entities'

import PageContext, { DefaultContext } from '@contexts/pageContext'
import BlankState from '@components/BlankState'

import { LayoutTemplateModel } from '@models/types'

import { generateUrls } from '@hooks/useLayoutTemplate'
import useLayoutTemplates from '@hooks/useLayoutTemplates'
import useSelector from '@hooks/useSelector'

import PageFilterModule from '@components/PageFilterModule'

import layouTemplateImage from './assets/layoutTemplateImage.svg'
import LayoutTemplateGridTile from '../LayoutTemplateGridTile'

import defaultRequestOptions from '../../../../defaultRequestOptions'

import type { PageContextPayload } from '../../index'

type SetStateType = (state: Partial<typeof defaultState>) => void

type StateType = {
  selectedPublicationId: number,
}

const buildPageFilterFields = (entities: EntitiesState, state: StateType, setState: SetStateType) => {
  const { publications } = entities
  const { selectedPublicationId } = state

  const publicationProducts = Object.values(publications).map(publication => publication?.products).flat(1)

  const selectedPublicationProducts = selectedPublicationId
    ? digObject(publications[selectedPublicationId], 'products', [])
    : publicationProducts

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      callbacks: { onChange: e => setState({ selectedPublicationId: e }) },

      entityKey: 'publication_id',
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'publication_id',
      label: 'Publications',
      values: Object.values(publications).map(publication => ({ key: publication.id, label: publication.title })),

    },
    {
      entityKey: 'publication_products',
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All Publications Products',
        inputComponent: 'select',
        style,
      },
      key: 'publication_product_id',
      label: 'Publication Products',
      values: selectedPublicationProducts.map(publication => ({ key: publication.id, label: publication.title })),
    },
    {
      entityKey: 'limit',
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: '100',
        inputComponent: 'select',
        style,
      },
      key: 'limit',
      label: 'Limit',
      values: [
        { key: 150, label: '150' },
        { key: 200, label: '200' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: object,
}

interface QueryParamsType {
  hidden?: string,
}

const defaultState = {
  selectedPublicationId: null,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const [state, setState] = useState(defaultState)

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const entities = useSelector(reduxState => reduxState.entities)

  const { hidden } : QueryParamsType = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden === 'true'

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const layoutTemplatesPayload = useLayoutTemplates({
    filters: {
      hidden: isHidden,
      ...pageFilters,
    },
    requestOptions: {
      ...defaultRequestOptions.layoutTemplate,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredLayoutTemplates,
    filteredLayoutTemplatesCount,
    hasFilteredLayoutTemplates,
    loading,
  } = layoutTemplatesPayload

  const { layoutTemplatesIndexUrl, archivedLayoutTemplatesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Layout Templates | Template Manager</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey={String(isHidden)}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: layoutTemplatesIndexUrl,
            icon: faHome,
            key: 'false',
            title: 'Layout Templates',
          },
          {
            href: archivedLayoutTemplatesIndexUrl,
            icon: faArchive,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Layout Templates"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Layout Templates">

                <Box flexDirection="column">
                  {!loading && !hasFilteredLayoutTemplates && (
                  <Box padding="large">
                    <BlankState
                      imageUrl={layouTemplateImage}
                      title="No Layout Templates"
                      subtitle="Try adjusting or clearing your filters for more results."
                    />
                  </Box>
                  )}

                  {!loading && hasFilteredLayoutTemplates && (
                    <Grid
                      gridGap="medium"
                      gridAutoRows="1fr"
                      gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                      gridTemplateRows="repeat(auto-fill, minmax(240px, 1fr))"
                      padding="large"
                    >
                      {filteredLayoutTemplates.map((layoutTemplate: LayoutTemplateModel) => (
                        <LayoutTemplateGridTile
                          key={layoutTemplate.id}
                          layoutTemplate={layoutTemplate}
                        />
                      ))}
                    </Grid>
                  )}

                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredLayoutTemplatesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(entities, state, setState)}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
